import { LoanStatus } from '@nemuru-eng/js-api-client';

export const prepareStatus = (loan) => {
  let status = loan.status;

  switch (status) {
    case statusNames.STATUS_INITIAL:
      if (loan.borrower_id) {
        status = statusNames.STATUS_OPENED;
      }
      break;
    case statusNames.STATUS_ACTIVE_TRIAL:
    case statusNames.STATUS_ACTIVE:
    case statusNames.STATUS_ENDED:
      status = statusNames.STATUS_FRONT_COMPLETED;
      break;
    default:
      break;
  }
  // \\if (loan.loan_asset)

  return { status, ...statusDefinitions[status] };
};

export const getCertificationEnabledStatus = (loan, certLoanParams) => {
  let preparedStatus = prepareStatus(loan);
  let status = preparedStatus.status;
  const loanAsset = loan.loan_asset;
  if (status && loanAsset) {
    if (loanAsset.loan_asset_steps.length === 0) {
      switch (status) {
        case statusNames.STATUS_DOCS_APPROVED:
        case statusNames.STATUS_FRONT_COMPLETED:
          status = statusNames.STATUS_FRONT_INITIAL_CERTIFICATION;
          break;
        default:
          break;
      }
    } else {
      const pendingCertificationsAmount = certLoanParams && certLoanParams.pendingCertificationsAmount;
      if (status === statusNames.STATUS_FRONT_COMPLETED && pendingCertificationsAmount !== 0) {
        status = statusNames.STATUS_FRONT_PARTIALLY_CERTIFICATED;
      }
    }
  }
  return status;
};

export const ALLOWED_LOAN_STATUSES_FOR_MULTI_SELECT_FILTER = [
  LoanStatus.STATUS_INITIAL,
  LoanStatus.STATUS_ANALYSING,
  LoanStatus.STATUS_LENDER_HAS_BEEN_SELECTED,
  LoanStatus.STATUS_CONTRACT_SIGNED,
  LoanStatus.STATUS_DOCS_APPROVED,
  LoanStatus.STATUS_ACTIVE,
  LoanStatus.STATUS_DENIED,
  LoanStatus.STATUS_CANCELLED,
  LoanStatus.STATUS_DESISTED,
  LoanStatus.STATUS_EXPIRED,
];

export const statusNames = {
  STATUS_FRONT_INITIAL_CERTIFICATION: 'status_front_initial_certification',
  STATUS_FRONT_PARTIALLY_CERTIFICATED: 'status_front_partially_certificated',
  STATUS_FRONT_COMPLETED: 'status_front_completed',
  STATUS_OPENED: 'status_opened',
  STATUS_CONTRACT_SENT: 'status_contract_sent',
  ...LoanStatus,
};

export const DISABLED_STATUSES = [
  statusNames.STATUS_CANCELLED_TEST,
  statusNames.STATUS_CANCELLED,
  statusNames.STATUS_DESISTED,
  statusNames.STATUS_EXPIRED,
];

export const statusSections = {
  PENDING: 'STATUS_SECTION_PENDING',
  ANALYSING: 'STATUS_SECTION_ANALYSING',
  APPROVED: 'STATUS_SECTION_APPROVED',
  FORMALIZED: 'STATUS_SECTION_CONFIRMED',
  DENIED: 'STATUS_SECTION_DENIED',
  CANCELLED: 'STATUS_SECTION_CANCELLED',
  DESISTED: 'STATUS_SECTION_DESISTED',
  EXPIRED: 'STATUS_SECTION_EXPIRED',
};

export const statusSectionsHierarchy = {
  [statusSections.CANCELLED]: 0,
  [statusSections.PENDING]: 0,
  [statusSections.DESISTED]: 0,
  [statusSections.ANALYSING]: 10,
  [statusSections.DENIED]: 10,
  [statusSections.APPROVED]: 20,
  [statusSections.FORMALIZED]: 30,
};

export const statusDefinitions = {
  [statusNames.STATUS_INITIAL]: {
    progressOrder: statusSectionsHierarchy[statusSections.PENDING],
    type: 'sent',
    colorPaletteName: 'info',
    colorType: 'dark',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_UNOPENED',
    DESCRIPTION_T_ID: 'STATUS_DESC_UNOPENED',
    SECTION_T_ID: statusSections.PENDING,
    showOnlyWhenCertificationsAvailable: true,
    nextStatus: [statusNames.STATUS_OPENED],
  },
  [statusNames.STATUS_OPENED]: {
    progressOrder: statusSectionsHierarchy[statusSections.PENDING] + 1,
    type: 'sent',
    colorPaletteName: 'info',
    colorType: 'dark',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_INITIATED',
    DESCRIPTION_T_ID: 'STATUS_DESC_INITIATED',
    SECTION_T_ID: statusSections.PENDING,
    nextStatus: [statusNames.STATUS_ANALYSING],
  },
  [statusNames.STATUS_ANALYSING]: {
    progressOrder: statusSectionsHierarchy[statusSections.ANALYSING],
    type: 'analysing',
    colorPaletteName: 'info',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_ANALYSING',
    DESCRIPTION_T_ID: 'STATUS_DESC_ANALYSING',
    SECTION_T_ID: statusSections.ANALYSING,
    nextStatus: [statusNames.STATUS_DENIED, statusNames.STATUS_LENDER_HAS_BEEN_SELECTED],
  },
  [statusNames.STATUS_DENIED]: {
    progressOrder: statusSectionsHierarchy[statusSections.DENIED],
    type: 'denied',
    colorPaletteName: 'error',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_DENIED',
    DESCRIPTION_T_ID: 'STATUS_DESC_DENIED',
    SECTION_T_ID: statusSections.DENIED,
  },
  [statusNames.STATUS_LENDER_HAS_BEEN_SELECTED]: {
    progressOrder: statusSectionsHierarchy[statusSections.APPROVED],
    type: 'approved',
    colorPaletteName: 'success',
    colorType: 'dark',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_LENDER_HAS_BEEN_SELECTED',
    DESCRIPTION_T_ID: 'STATUS_DESC_LENDER_HAS_BEEN_SELECTED',
    SECTION_T_ID: statusSections.APPROVED,
    nextStatus: [statusNames.STATUS_CONTRACT_SIGNED],
  },
  [statusNames.STATUS_CONTRACT_SIGNED]: {
    progressOrder: statusSectionsHierarchy[statusSections.APPROVED] + 2,
    type: 'approved',
    colorPaletteName: 'success',
    colorType: 'dark',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_CONTRACT_SIGNED',
    DESCRIPTION_T_ID: 'STATUS_DESC_CONTRACT_SIGNED',
    SECTION_T_ID: statusSections.APPROVED,
    nextStatus: [statusNames.STATUS_DOCS_APPROVED],
  },
  [statusNames.STATUS_DOCS_APPROVED]: {
    progressOrder: statusSectionsHierarchy[statusSections.FORMALIZED],
    type: 'active',
    colorPaletteName: 'success',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_DOCUMENTATION',
    DESCRIPTION_T_ID: 'STATUS_DESC_DOCUMENTATION',
    SECTION_T_ID: statusSections.FORMALIZED,
    nextStatus: [statusNames.STATUS_ACTIVE_TRIAL],
  },
  [statusNames.STATUS_FRONT_INITIAL_CERTIFICATION]: {
    progressOrder: statusSectionsHierarchy[statusSections.FORMALIZED],
    type: 'active',
    colorPaletteName: 'success',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_INITIAL_CERTIFICATION',
    DESCRIPTION_T_ID: 'STATUS_DESC_INITIAL_CERTIFICATION',
    SECTION_T_ID: statusSections.FORMALIZED,
  },
  [statusNames.STATUS_FRONT_PARTIALLY_CERTIFICATED]: {
    progressOrder: statusSectionsHierarchy[statusSections.FORMALIZED],
    type: 'active',
    colorPaletteName: 'success',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_PARTIALLY_CERTIFICATED',
    DESCRIPTION_T_ID: 'STATUS_DESC_PARTIALLY_CERTIFICATED',
    SECTION_T_ID: statusSections.FORMALIZED,
  },
  [statusNames.STATUS_ACTIVE_TRIAL]: {
    progressOrder: statusSectionsHierarchy[statusSections.FORMALIZED] + 1,
    type: 'active',
    colorPaletteName: 'success',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_ACTIVE',
    DESCRIPTION_T_ID: 'STATUS_DESC_ACTIVE',
    SECTION_T_ID: statusSections.FORMALIZED,
    nextStatus: [statusNames.STATUS_ACTIVE],
  },
  [statusNames.STATUS_UNPAID]: {
    progressOrder: statusSectionsHierarchy[statusSections.FORMALIZED] + 2,
    type: 'active',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_UNPAID',
    DESCRIPTION_T_ID: 'STATUS_DESC_UNPAID',
    SECTION_T_ID: statusSections.FORMALIZED,
  },
  [statusNames.STATUS_ACTIVE]: {
    progressOrder: statusSectionsHierarchy[statusSections.FORMALIZED] + 1,
    type: 'active',
    colorPaletteName: 'success',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_ACTIVE',
    DESCRIPTION_T_ID: 'STATUS_DESC_ACTIVE',
    SECTION_T_ID: statusSections.FORMALIZED,
  },
  [statusNames.STATUS_FRONT_COMPLETED]: {
    progressOrder: statusSectionsHierarchy[statusSections.FORMALIZED] + 2,
    type: 'active',
    colorPaletteName: 'success',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_FRONT_COMPLETED',
    DESCRIPTION_T_ID: 'STATUS_DESC_FRONT_COMPLETED',
    SECTION_T_ID: statusSections.FORMALIZED,
  },
  [statusNames.STATUS_ENDED]: {
    progressOrder: statusSectionsHierarchy[statusSections.FORMALIZED] + 2,
    type: 'active',
    colorPaletteName: 'success',
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_ENDED',
    DESCRIPTION_T_ID: 'STATUS_DESC_ENDED',
    SECTION_T_ID: statusSections.FORMALIZED,
  },
  [statusNames.STATUS_DESISTED]: {
    type: 'cancelled',
    colorPaletteName: undefined,
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_DESISTED',
    DESCRIPTION_T_ID: 'STATUS_DESC_DESISTED',
    SECTION_T_ID: statusSections.DESISTED,
  },
  [statusNames.STATUS_EXPIRED]: {
    type: 'cancelled',
    colorPaletteName: undefined,
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_EXPIRED',
    DESCRIPTION_T_ID: 'STATUS_DESC_EXPIRED',
    SECTION_T_ID: statusSections.DESISTED,
  },
  [statusNames.STATUS_CANCELLED]: {
    progressOrder: -1,
    type: 'cancelled',
    colorPaletteName: undefined,
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_CANCELLED',
    DESCRIPTION_T_ID: 'STATUS_DESC_CANCELLED',
    SECTION_T_ID: statusSections.CANCELLED,
  },
  [statusNames.STATUS_CANCELLED_TEST]: {
    progressOrder: -1,
    type: 'cancelled',
    colorPaletteName: undefined,
    colorType: 'main',
    PROGRESS_LABEL_T_ID: 'STATUS_NEXT_STEP_CANCELLED_TEST',
    DESCRIPTION_T_ID: 'STATUS_DESC_CANCELLED_TEST',
    SECTION_T_ID: statusSections.CANCELLED,
  },
};

export const CANCEL_DISABLED_STATUS = [
  statusNames.STATUS_CANCELLED,
  statusNames.STATUS_CANCELLED_TEST,
  statusNames.STATUS_ENDED,
  statusNames.STATUS_UNPAID,
  statusNames.STATUS_ACTIVE,
  statusNames.STATUS_ACTIVE_TRIAL,
  statusNames.STATUS_DOCS_APPROVED,
  statusNames.STATUS_DENIED,
];

export const isDealCanceledTest = (deal) => {
  if (deal) {
    return deal.status === statusNames.STATUS_CANCELLED_TEST;
  }
};
